import {buildInitialGridState} from './utils';
import {GridAction, GridActions, GridState, Order} from './types';
import {
  dynamicSearchGridId,
  modelListGridId,
  userListGridId,
  incidentListGridId,
  notificationsListGridId,
  externalizationListGridId,
  externalUserDetailGridId,
  scenarioExecutionQueueGridId,
} from './gridIds';
import {AuthAction, AuthActions} from '../auth/types';
import {AsyncActionPhase} from '../asyncUtilsTypes';

export type GridStoreState = {[gridId: string]: GridState};

const MODEL_LIST_STORAGE_KEY = 'mrGridState:modelList';

type PersistedModelListState = Pick<GridState, 'filter' | 'search' | 'viewFavorites' | 'viewMyModels'>;

const loadPersistedModelListState = (): Partial<GridState> => {
  try {
    const raw = localStorage.getItem(MODEL_LIST_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as PersistedModelListState;
  } catch {
    return {};
  }
};

const saveModelListState = (state: GridState): void => {
  try {
    const toSave: PersistedModelListState = {
      filter: state.filter,
      search: '',
      viewFavorites: state.viewFavorites,
      viewMyModels: state.viewMyModels,
    };
    localStorage.setItem(MODEL_LIST_STORAGE_KEY, JSON.stringify(toSave));
  } catch {
    // ignore storage errors (e.g. private browsing quota)
  }
};

const clearPersistedModelListState = (): void => {
  try {
    localStorage.removeItem(MODEL_LIST_STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const gridInitialState: GridStoreState = {
  [modelListGridId]: buildInitialGridState({
    orderBy: 'createdOn',
    order: Order.DESC,
    // @ts-ignore
    filter: {
      modelTypes: [],
      global: [],
      countries: [],
      stewards: [],
      creators: [],
      products: [],
      diseases: [],
      type: [],
      therapeuticAreas: [],
      platforms: [],
      status: ['Draft', 'Published'],
    },
    ...loadPersistedModelListState(),
  }),
  [dynamicSearchGridId]: buildInitialGridState({
    orderBy: 'createdOn',
    order: Order.DESC,
    // @ts-ignore
    filter: {
      model_type: [],
      global: [],
      country: [],
      product: [],
      disease: [],
      therapeutic_area: [],
      study_number: [],
      platform: [],
    },
  }),
  [userListGridId]: buildInitialGridState({
    orderBy: 'lastName',
    order: Order.ASC,
  }),
  [externalizationListGridId]: buildInitialGridState({
    orderBy: 'lastName',
    order: Order.ASC,
  }),
  [incidentListGridId]: buildInitialGridState({
    orderBy: 'createdOn',
    order: Order.DESC,
  }),
  [notificationsListGridId]: buildInitialGridState({
    rowsPerPage: 20,
  }),
  [externalUserDetailGridId]: buildInitialGridState({
    rowsPerPage: 20,
  }),
  [scenarioExecutionQueueGridId]: buildInitialGridState({
    orderBy: 'createdOn',
    order: Order.DESC,
    rowsPerPage: 50,
  }),
};

export const gridReducer = (state = gridInitialState, action: GridAction | AuthAction): GridStoreState => {
  switch (action.type) {
    case GridActions.UPDATE_STATE: {
      const nextGridState = {
        ...state[action.gridId],
        ...action.update,
      };
      if (action.gridId === modelListGridId) {
        saveModelListState(nextGridState);
      }
      return {
        ...state,
        [action.gridId]: nextGridState,
      };
    }

    case GridActions.SET_STATE: {
      const nextGridState = {
        ...gridInitialState[action.gridId],
        ...action.state,
      };
      if (action.gridId === modelListGridId) {
        saveModelListState(nextGridState);
      }
      return {
        ...state,
        [action.gridId]: nextGridState,
      };
    }

    case AuthActions.AUTH_LOGOUT:
      if (action.phase === AsyncActionPhase.SUCCESS) {
        clearPersistedModelListState();
        return gridInitialState;
      }
      return state;

    default:
      return state;
  }
};
